/**
 * ResourceInfoPopup — Sovereign Emerald resource info popup
 *
 * Design: cream banknote-paper card with engraved racing-green ink,
 * champagne-gold metallic accents, hairline rules, soft role-colour
 * tints, and smooth spring animations. Light and printed — never dark.
 */

import { AnimatePresence, motion } from 'framer-motion';
import {
  Building2,
  CalendarClock,
  Layers,
  Mail,
  Phone,
  Shield,
  X,
} from 'lucide-react';
import { useEffect } from 'react';
import type {
  EpcProjectMembershipRow,
  EpcProjectOrgUnit,
  EpcProjectTeamRole,
} from '../../../../services/epcProjectService';
import { ROLE_LABEL, isGovernanceRole } from './teamCapabilities';
import { initials } from './hierarchyLayout';
import type { EpcScheduleTaskRow } from '../../types/scheduleTask';

/* ══════════ ROLE PALETTE (Sovereign Emerald — greens + champagne only) ══════════ */
const ROLE_PALETTE: Record<
  EpcProjectTeamRole,
  { gradA: string; gradB: string; glow: string; tint: string; text: string }
> = {
  epc_head:            { gradA: '#0B3B2E', gradB: '#0B3B2E', glow: '#8A5A08', tint: 'rgba(201,169,97,0.16)', text: '#8A5A08' },
  pmo:                 { gradA: '#1F5A47', gradB: '#0B3B2E', glow: '#1F5A47', tint: 'rgba(51,146,116,0.12)', text: '#1F5A47' },
  project_head:        { gradA: '#264f3c', gradB: '#0B3B2E', glow: '#1F5A47', tint: 'rgba(51,146,116,0.12)', text: '#1F5A47' },
  project_coordinator: { gradA: '#339274', gradB: '#1F5A47', glow: '#1F5A47', tint: 'rgba(51,146,116,0.12)', text: '#1F5A47' },
  department_head:     { gradA: '#C9A961', gradB: '#8A5A08', glow: '#8A5A08', tint: 'rgba(201,169,97,0.16)', text: '#8A5A08' },
  manager:             { gradA: '#2d5c47', gradB: '#1F5A47', glow: '#1F5A47', tint: 'rgba(51,146,116,0.12)', text: '#1F5A47' },
  site_engineer:       { gradA: '#339274', gradB: '#264f3c', glow: '#339274', tint: 'rgba(51,146,116,0.12)', text: '#1F5A47' },
};

function orgUnitId(o: EpcProjectOrgUnit): string {
  return String(
    (o as { _id?: string; id?: string })._id ??
    (o as { _id?: string; id?: string }).id ?? ''
  );
}

/* ══════════ TYPES ══════════ */
export interface ResourcePopupData {
  member: EpcProjectMembershipRow;
  allMemberships: EpcProjectMembershipRow[];
  orgUnits: EpcProjectOrgUnit[];
  availableDepts: EpcProjectOrgUnit[];
  canMove: boolean;
  canCopy: boolean;
  /** All schedule tasks assigned to this member. Empty when no schedule data. */
  memberTasks?: EpcScheduleTaskRow[];
}
export interface ResourcePopupActions {
  onCopyToDept: (targetOrgId: string, targetManagerId?: string) => void;
  onMoveToDept: (targetOrgId: string, targetManagerId?: string) => void;
  onClose: () => void;
}

/* ══════════ CHAMPAGNE GLEAM LAYER ══════════ */
function GlassShimmer() {
  return (
    <motion.div
      className="pointer-events-none absolute inset-0 rounded-3xl"
      style={{
        background:
          'linear-gradient(105deg, rgba(201,169,97,0.10) 0%, transparent 40%, rgba(201,169,97,0.05) 100%)',
      }}
      animate={{ opacity: [0.6, 1, 0.6] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      aria-hidden
    />
  );
}

/* Animated soft ink wash in background (kept subtle — printed, not glowing) */
function AuroraBlob({ color, style }: { color: string; style?: React.CSSProperties }) {
  return (
    <motion.div
      className="pointer-events-none absolute rounded-full blur-3xl"
      style={{ background: color, opacity: 0.07, width: 160, height: 160, ...style }}
      animate={{ scale: [1, 1.15, 1], opacity: [0.05, 0.09, 0.05] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      aria-hidden
    />
  );
}

/* ══════════ PULSE DOTS ══════════ */
function PulseDots({ color }: { color: string }) {
  return (
    <div className="flex gap-1">
      {[0, 1, 2, 3, 4].map((i) => (
        <motion.div
          key={i}
          className="h-1.5 w-1.5 rounded-full"
          style={{ background: color }}
          animate={{ opacity: [0.2, 1, 0.2], scale: [0.8, 1.1, 0.8] }}
          transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.18, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}

/* ══════════ DEPT TAG ══════════ */
function DeptTag({ name, index }: { name: string; index: number }) {
  return (
    <motion.span
      initial={{ opacity: 0, scale: 0.75, y: 4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay: index * 0.06, type: 'spring', stiffness: 400, damping: 22 }}
      className="inline-flex items-center gap-1 rounded-xl px-2.5 py-1 text-[10px] font-semibold"
      style={{
        background: '#F8F2E2',
        border: '1px solid rgba(11,59,46,0.18)',
        color: '#0B3B2E',
      }}
    >
      <Building2 className="h-2.5 w-2.5 opacity-60" />
      {name}
    </motion.span>
  );
}

/* ══════════ MAIN POPUP ══════════ */
export function ResourceInfoPopup({
  data,
  actions,
}: {
  data: ResourcePopupData;
  actions: ResourcePopupActions;
}) {
  const { member, allMemberships, orgUnits, canMove, canCopy } = data;
  const memberTasks = (data.memberTasks ?? []).filter(
    (t) => t.start || t.finish,
  );
  const pal = ROLE_PALETTE[member.role];
  const av  = initials(member.userName, member.userId);
  const isDeptRole = !isGovernanceRole(member.role);

  /* Escape key */
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') actions.onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [actions]);

  const deptNames = allMemberships
    .filter((m) => m.orgUnitId)
    .map((m) => {
      const ou = orgUnits.find((o) => orgUnitId(o) === m.orgUnitId);
      return ou?.name ?? m.orgUnitName ?? m.orgUnitId;
    })
    .filter(Boolean) as string[];

  const deptCount = deptNames.length;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.86, y: 18 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 10 }}
      transition={{ type: 'spring', stiffness: 480, damping: 30 }}
      className="relative w-[310px] overflow-hidden rounded-3xl"
      style={{
        /* banknote-paper base */
        background: 'rgba(255,254,251,0.96)',
        border: '1px solid rgba(11,59,46,0.18)',
        boxShadow: [
          '0 1px 2px rgba(11,59,46,0.04)',
          '0 16px 40px -24px rgba(11,59,46,0.20)',
          '0 32px 64px -32px rgba(42,31,23,0.25)',
        ].join(', '),
      }}
    >
      {/* ── soft ink washes ── */}
      <AuroraBlob color={pal.gradA} style={{ top: -60, left: -40 }} />
      <AuroraBlob color={pal.gradB} style={{ bottom: -40, right: -30 }} />

      {/* ── champagne gleam ── */}
      <GlassShimmer />

      {/* ── top accent line ── */}
      <div
        className="absolute inset-x-0 top-0 h-[1.5px] rounded-t-3xl"
        style={{ background: 'linear-gradient(90deg, transparent 5%, #C9A96190 40%, #C9A961 60%, transparent 95%)' }}
      />

      <div className="relative">
        {/* ══ HEADER ══ */}
        <div className="flex items-start gap-3.5 px-4 pt-5 pb-4">
          {/* avatar */}
          <div className="relative shrink-0">
            <motion.div
              className="flex items-center justify-center rounded-2xl font-black text-base text-white"
              style={{
                width: 52, height: 52,
                background: `linear-gradient(135deg, ${pal.gradA}, ${pal.gradB})`,
                boxShadow: '0 0 0 2px rgba(255,254,251,0.9), 0 8px 18px -10px rgba(11,59,46,0.40)',
              }}
              animate={{ boxShadow: ['0 0 0 2px rgba(255,254,251,0.9), 0 6px 14px -10px rgba(11,59,46,0.30)', '0 0 0 2px rgba(255,254,251,1), 0 10px 20px -10px rgba(11,59,46,0.45)', '0 0 0 2px rgba(255,254,251,0.9), 0 6px 14px -10px rgba(11,59,46,0.30)'] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            >
              {av}
            </motion.div>
            {/* online dot */}
            <span
              className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2"
              style={{
                background: '#339274',
                borderColor: 'rgba(255,254,251,1)',
                boxShadow: '0 1px 3px rgba(11,59,46,0.25)',
              }}
            />
          </div>

          {/* name + role */}
          <div className="min-w-0 flex-1 pt-0.5">
            <p className="truncate text-[14px] font-black leading-tight" style={{ color: '#0B3B2E' }}>
              {member.userName ?? 'Unknown'}
            </p>
            {member.userEmail ? (
              <p className="mt-0.5 flex items-center gap-1 truncate text-[10px]" style={{ color: '#7C8A82' }}>
                <Mail className="h-2.5 w-2.5 shrink-0" />
                {member.userEmail}
              </p>
            ) : null}
            {/* role pill */}
            <div className="mt-2">
              <span
                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[9px] font-black uppercase tracking-widest"
                style={{
                  background: pal.tint,
                  border: `1px solid ${pal.glow}45`,
                  color: pal.text,
                }}
              >
                {ROLE_LABEL[member.role]}
              </span>
            </div>
          </div>

          {/* close */}
          <button
            type="button"
            onClick={actions.onClose}
            className="shrink-0 rounded-xl p-1.5 transition-all hover:scale-110"
            style={{
              background: '#fcfaf3',
              border: '1px solid rgba(11,59,46,0.28)',
              color: '#0B3B2E',
            }}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* ── hairline divider ── */}
        <div className="mx-4 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(11,59,46,0.20), transparent)' }} />

        {/* ══ BODY ══ */}
        <div className="space-y-4 px-4 py-4">

          {/* ── Dept assignments ── */}
          <div>
            <div className="mb-2 flex items-center gap-1.5">
              <Layers className="h-3 w-3" style={{ color: pal.glow }} />
              <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: pal.glow }}>
                Departments ({deptCount})
              </span>
            </div>
            {deptNames.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {deptNames.map((name, i) => <DeptTag key={name} name={name} index={i} />)}
              </div>
            ) : (
              <span className="text-[10px]" style={{ color: '#7C8A82' }}>No department assigned</span>
            )}
          </div>

          {/* ── Allocated tasks ── */}
          {memberTasks.length > 0 ? (
            <div>
              <div className="mb-2 flex items-center gap-1.5">
                <CalendarClock className="h-3 w-3" style={{ color: pal.glow }} />
                <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: pal.glow }}>
                  Allocated tasks ({memberTasks.length})
                </span>
              </div>
              <ul className="space-y-1">
                {memberTasks.slice(0, 6).map((t, i) => {
                  const name = String(t.name ?? t.taskName ?? t.title ?? '—');
                  const start = t.start ? String(t.start).slice(0, 10) : null;
                  const finish = t.finish ? String(t.finish).slice(0, 10) : null;
                  const dept = t.epcOrgUnitId
                    ? (orgUnits.find((o) => orgUnitId(o) === t.epcOrgUnitId)?.name ?? null)
                    : null;
                  return (
                    <li
                      key={i}
                      className="flex items-start gap-1.5 rounded-lg px-2 py-1.5"
                      style={{ background: '#F8F2E2', border: '1px solid rgba(11,59,46,0.14)' }}
                    >
                      <CalendarClock className="mt-0.5 h-3 w-3 shrink-0 opacity-40" style={{ color: '#0B3B2E' }} />
                      <div className="min-w-0">
                        <p className="truncate text-[10px] font-semibold" style={{ color: '#2A1F17' }}>{name}</p>
                        <p className="text-[9px]" style={{ color: '#7C8A82' }}>
                          {start && finish ? `${start} → ${finish}` : start ?? finish ?? ''}
                          {dept ? ` · ${dept}` : ''}
                        </p>
                      </div>
                    </li>
                  );
                })}
                {memberTasks.length > 6 ? (
                  <li className="text-center text-[9.5px]" style={{ color: '#7C8A82' }}>
                    + {memberTasks.length - 6} more
                  </li>
                ) : null}
              </ul>
            </div>
          ) : null}

          {/* ── Read-only note ── */}
          {isDeptRole && !canMove && !canCopy ? (
            <div
              className="flex items-center gap-2 rounded-2xl px-3.5 py-2.5"
              style={{
                background: '#F8F2E2',
                border: '1px solid rgba(11,59,46,0.14)',
              }}
            >
              <Shield className="h-3.5 w-3.5 shrink-0" style={{ color: '#7C8A82' }} />
              <p className="text-[10px]" style={{ color: '#7C8A82' }}>
                Governance can move members between departments. Managers can do this for their engineers.
              </p>
            </div>
          ) : null}
        </div>

        {/* ── hairline divider ── */}
        <div className="mx-4 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(11,59,46,0.14), transparent)' }} />

        {/* ══ FOOTER ══ */}
        <div className="flex items-center gap-2 px-4 py-3">
          <Phone className="h-3 w-3 shrink-0" style={{ color: '#7C8A82' }} />
          <span className="text-[10px] font-semibold" style={{ color: '#2A1F17' }}>
            {member.userMobile ?? '—'}
          </span>
          <div className="ml-auto">
            <PulseDots color={pal.glow} />
          </div>
        </div>
      </div>

      {/* ── bottom accent line ── */}
      <div
        className="absolute inset-x-0 bottom-0 h-px rounded-b-3xl"
        style={{ background: 'linear-gradient(90deg, transparent, #C9A96150, transparent)' }}
      />
    </motion.div>
  );
}

/* ══════════ OVERLAY ══════════ */
export function ResourcePopupOverlay({
  data,
  actions,
}: {
  data: ResourcePopupData | null;
  actions: ResourcePopupActions;
}) {
  return (
    <AnimatePresence>
      {data ? (
        <>
          {/* blurred backdrop */}
          <motion.div
            key="bd"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(42,31,23,0.35)', backdropFilter: 'blur(4px)' }}
            onClick={actions.onClose}
          />
          {/* popup centred */}
          <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="pointer-events-auto">
              <ResourceInfoPopup
                data={data}
                actions={actions}
              />
            </div>
          </div>
        </>
      ) : null}
    </AnimatePresence>
  );
}
